import { supabase } from '../lib/supabase'
import { financeiroExtratosContaService } from './financeiroExtratosConta'
import {
  FinanceiroPastaJaProcessadaError,
  type AppendComissoesIcatuRowsInput,
  type BeginComissoesIcatuPastaInput,
  type CompleteComissoesIcatuPastaInput,
  type FinanceiroAnaliseComparativaFilters,
  type FinanceiroAnaliseComparativaLinha,
  type FinanceiroAnaliseComparativaResult,
  type FinanceiroAnaliseComissaoItem,
  type FinanceiroAnaliseComissaoItensFilters,
  type FinanceiroAnaliseComissaoItensResult,
  type FinanceiroComissaoRow,
  type FinanceiroComissoesConsultaFilters,
  type FinanceiroComissoesConsultaResult,
  type FinanceiroComissoesResumo,
  type FinanceiroComissoesIcatuLinha,
  type FinanceiroComissoesIcatuPasta,
} from '../types/financeiro'

const BATCH_SIZE = 500

const FOLDERS_TABLE = 'finan_icatu_commission_folders'
const ROWS_TABLE = 'finan_icatu_commissions'

const COMISSAO_SORT_COLUMNS = new Set([
  'id',
  'folder_name',
  'file_name',
  'codigo_corretor',
  'nome_corretor',
  'cpf_cnpj',
  'apolice',
  'numero_sub',
  'modulo',
  'estipulante',
  'sub_estipulante',
  'fatura',
  'competencia',
  'valor_apropriado',
  'tipo_comissao',
  'parcela_inicial',
  'numero_grupo_cobertura',
  'valor_base',
  'percentual_comissao',
  'valor_comissao',
  'data_emissao',
  'data_apropriacao',
  'valor_premio_bruto',
  'valor_premio_tarifario',
  'data_movimento',
  'data_agendamento',
  'data_pagamento',
  'data_pagamento_efetivo',
  'status_recibo',
  'nr_cobranca',
  'created_at',
])

function escapeIlike(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_')
}

type DocumentoConsulta = {
  variants: string[]
}

/**
 * CPF (11 dígitos) ou CNPJ (14, numérico ou alfanumérico), com ou sem máscara.
 * A busca usa igualdade em cpf_cnpj. O ilike com % entre cada dígito varre a tabela
 * inteira e o Postgres cancela com statement_timeout.
 */
function documentoConsulta(search: string): DocumentoConsulta | null {
  const compact = search.replace(/[.\-\/\s]/g, '').toUpperCase()
  const isCpf = /^\d{11}$/.test(compact)
  const isCnpj = /^[A-Z0-9]{12}\d{2}$/.test(compact)
  if (!isCpf && !isCnpj) return null

  const formatted = isCnpj
    ? `${compact.slice(0, 2)}.${compact.slice(2, 5)}.${compact.slice(5, 8)}/${compact.slice(8, 12)}-${compact.slice(12)}`
    : `${compact.slice(0, 3)}.${compact.slice(3, 6)}.${compact.slice(6, 9)}-${compact.slice(9)}`

  return { variants: [...new Set([compact, formatted])] }
}

function selectedFolderNames(filters: FinanceiroComissoesConsultaFilters): string[] {
  return [...new Set((filters.folderNames ?? []).map((name) => name.trim()).filter(Boolean))]
}

function hasLineFilter(filters: FinanceiroComissoesConsultaFilters): boolean {
  return Boolean(
    filters.dataPagamentoEfetivo?.trim() ||
      filters.tipoComissao?.trim() ||
      filters.statusRecibo?.trim() ||
      filters.search?.trim()
  )
}

/** Número de série do Excel para a data civil (mesma época usada na exibição). */
function excelSerialFromIsoDate(isoDate: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate)
  if (!match) return null
  const utc = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  const epoch = Date.UTC(1899, 11, 30)
  return Math.round((utc - epoch) / 86400000)
}

/**
 * Formas em que a planilha grava a mesma data: DD/MM/AAAA, ISO e serial Excel.
 */
function dataPagamentoEfetivoLookups(isoDate: string): string[] {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim())
  if (!match) return [isoDate.trim()]
  const [, year, month, day] = match
  const dayN = String(Number(day))
  const monthN = String(Number(month))
  const shortYear = year.slice(2)
  const serial = excelSerialFromIsoDate(isoDate.trim())
  const values = [
    `${day}/${month}/${year}`,
    `${dayN}/${month}/${year}`,
    `${day}/${monthN}/${year}`,
    `${dayN}/${monthN}/${year}`,
    `${day}/${month}/${shortYear}`,
    `${dayN}/${monthN}/${shortYear}`,
    `${year}-${month}-${day}`,
  ]
  if (serial != null) {
    values.push(String(serial), `${serial}.0`)
  }
  return [...new Set(values)]
}

function quoteFilterValue(value: string): string {
  if (/[,.:"()]/.test(value)) return `"${value.replace(/"/g, '""')}"`
  return value
}

type ComissaoFilterQuery = {
  eq: (column: string, value: string) => ComissaoFilterQuery
  in: (column: string, values: string[]) => ComissaoFilterQuery
  or: (filters: string) => ComissaoFilterQuery
}

type ComissaoPageResult = {
  data: unknown
  error: { message?: string } | null
  count: number | null
}

/** O builder do PostgREST devolve a si mesmo; o segundo .order() precisa do mesmo tipo do primeiro. */
type ComissaoOrderedQuery = {
  order: (column: string, options: { ascending: boolean }) => ComissaoOrderedQuery
  range: (from: number, to: number) => Promise<ComissaoPageResult>
}

function applyComissaoFilters(
  query: ComissaoFilterQuery,
  filters: FinanceiroComissoesConsultaFilters
): ComissaoFilterQuery {
  const folderNames = selectedFolderNames(filters)
  if (folderNames.length === 1) {
    query = query.eq('folder_name', folderNames[0])
  } else if (folderNames.length > 1) {
    query = query.in('folder_name', folderNames)
  }
  const pagamento = filters.dataPagamentoEfetivo?.trim()
  if (pagamento) {
    // Só igualdade. ILIKE de prefixo não usa o índice btree e, no OR,
    // faz o Postgres varrer a tabela inteira (statement_timeout).
    // O .in() do cliente não aspas valores com ponto (serial Excel 45292.0).
    const parts = dataPagamentoEfetivoLookups(pagamento).map(
      (value) => `data_pagamento_efetivo.eq.${quoteFilterValue(value)}`
    )
    query = query.or(parts.join(','))
  }
  if (filters.tipoComissao?.trim()) {
    query = query.eq('tipo_comissao', filters.tipoComissao.trim())
  }
  if (filters.statusRecibo?.trim()) {
    query = query.eq('status_recibo', filters.statusRecibo.trim())
  }

  const search = filters.search?.trim()
  if (!search) return query

  const documento = documentoConsulta(search)
  if (documento) {
    return query.in('cpf_cnpj', documento.variants)
  }

  const term = escapeIlike(search.replace(/,/g, ' '))
  return query.or(
    [
      `nome_corretor.ilike.%${term}%`,
      `codigo_corretor.ilike.%${term}%`,
      `cpf_cnpj.ilike.%${term}%`,
      `apolice.ilike.%${term}%`,
      `fatura.ilike.%${term}%`,
    ].join(',')
  )
}

function rowToInsert(
  row: FinanceiroComissaoRow,
  folderId: string,
  uploadedBy: string | null
) {
  return {
    folder_id: folderId,
    folder_name: row.pasta,
    file_name: row.arquivo,
    codigo_corretor: row.codigoCorretor,
    nome_corretor: row.nomeCorretor,
    cpf_cnpj: row.cpfCnpj,
    apolice: row.apolice,
    numero_sub: row.numeroSub,
    modulo: row.modulo,
    estipulante: row.estipulante,
    sub_estipulante: row.subEstipulante,
    fatura: row.fatura,
    competencia: row.competencia,
    valor_apropriado: row.valorApropriado,
    tipo_comissao: row.tipoComissao,
    parcela_inicial: row.parcelaInicial,
    numero_grupo_cobertura: row.numeroGrupoCobertura,
    valor_base: row.valorBase,
    percentual_comissao: row.percentualComissao,
    valor_comissao: row.valorComissao,
    data_emissao: row.dataEmissao,
    data_apropriacao: row.dataApropriacao,
    valor_premio_bruto: row.valorPremioBruto,
    valor_premio_tarifario: row.valorPremioTarifario,
    data_movimento: row.dataMovimento,
    data_agendamento: row.dataAgendamento,
    data_pagamento: row.dataPagamento,
    data_pagamento_efetivo: row.dataPagamentoEfetivo,
    status_recibo: row.statusRecibo,
    nr_cobranca: row.nrCobranca,
    uploaded_by: uploadedBy,
  }
}

export const financeiroService = {
  /** Pastas finalizadas (status = complete). */
  async listPastasProcessadas(): Promise<FinanceiroComissoesIcatuPasta[]> {
    const { data, error } = await supabase
      .from(FOLDERS_TABLE)
      .select('*')
      .eq('status', 'complete')
      .order('folder_name', { ascending: true })

    if (error) throw error
    return (data ?? []) as FinanceiroComissoesIcatuPasta[]
  },

  async isPastaProcessada(pastaNome: string): Promise<boolean> {
    const { data, error } = await supabase
      .from(FOLDERS_TABLE)
      .select('id')
      .eq('folder_name', pastaNome)
      .eq('status', 'complete')
      .maybeSingle()

    if (error) throw error
    return !!data
  },

  /**
   * Remove pastas `processing` órfãs do próprio usuário (ex.: crash antigo).
   * Não apaga jobs recentes nem de outros usuários — evita matar import em outra aba/sessão.
   * CASCADE apaga linhas parciais. Retentativa imediata da mesma pasta é feita em `beginPasta`.
   */
  async cleanupIncompletePastas(userId: string | null | undefined): Promise<number> {
    if (!userId) return 0

    const staleBefore = new Date(Date.now() - 60 * 60 * 1000).toISOString()

    const { data, error } = await supabase
      .from(FOLDERS_TABLE)
      .delete()
      .eq('status', 'processing')
      .eq('processed_by', userId)
      .lt('processed_at', staleBefore)
      .select('id')

    if (error) throw error
    return data?.length ?? 0
  },

  /**
   * Abre a pasta com status=processing. Falha se já existir complete com o mesmo nome.
   */
  async beginPasta(
    input: BeginComissoesIcatuPastaInput
  ): Promise<FinanceiroComissoesIcatuPasta> {
    const pastaNome = input.pastaNome.trim()
    if (!pastaNome) throw new Error('Nome da pasta é obrigatório.')

    const already = await this.isPastaProcessada(pastaNome)
    if (already) throw new FinanceiroPastaJaProcessadaError(pastaNome)

    // Limpa sobra processing com o mesmo nome (unique em folder_name)
    const { error: cleanupError } = await supabase
      .from(FOLDERS_TABLE)
      .delete()
      .eq('folder_name', pastaNome)
      .eq('status', 'processing')

    if (cleanupError) throw cleanupError

    const { data: pasta, error: pastaError } = await supabase
      .from(FOLDERS_TABLE)
      .insert({
        folder_name: pastaNome,
        year: input.ano,
        root_folder_name: input.rootFolderName,
        files_ok: 0,
        files_error: 0,
        row_count: 0,
        status: 'processing',
        processed_by: input.userId,
        processed_at: new Date().toISOString(),
      })
      .select('*')
      .single()

    if (pastaError) {
      if (pastaError.code === '23505') {
        throw new FinanceiroPastaJaProcessadaError(pastaNome)
      }
      throw pastaError
    }

    return pasta as FinanceiroComissoesIcatuPasta
  },

  /** Insere linhas de um arquivo em lotes; não marca a pasta como completa. */
  async appendRows(input: AppendComissoesIcatuRowsInput): Promise<number> {
    if (input.rows.length === 0) return 0

    const payloads = input.rows.map((r) =>
      rowToInsert(r, input.folderId, input.userId)
    )
    const fileTotal = payloads.length
    let savedInFile = 0

    input.onProgress?.({
      pastaNome: input.pastaNome,
      savedRows: input.savedRowsBefore,
      totalRows: input.savedRowsBefore + fileTotal,
    })

    for (let i = 0; i < payloads.length; i += BATCH_SIZE) {
      const chunk = payloads.slice(i, i + BATCH_SIZE)
      const { error: rowsError } = await supabase.from(ROWS_TABLE).insert(chunk)
      if (rowsError) throw rowsError
      savedInFile += chunk.length
      input.onProgress?.({
        pastaNome: input.pastaNome,
        savedRows: input.savedRowsBefore + savedInFile,
        totalRows: input.savedRowsBefore + fileTotal,
      })
    }

    return savedInFile
  },

  async completePasta(input: CompleteComissoesIcatuPastaInput): Promise<void> {
    const payload: Record<string, unknown> = {
      status: 'complete',
      files_ok: input.arquivosOk,
      files_error: input.arquivosErro,
      row_count: input.rowCount,
      processed_at: new Date().toISOString(),
    }
    if (input.commissionTotal != null && Number.isFinite(input.commissionTotal)) {
      payload.commission_total = input.commissionTotal
    }

    const { error } = await supabase.from(FOLDERS_TABLE).update(payload).eq('id', input.folderId)
    if (!error) return

    const missingColumn =
      'commission_total' in payload &&
      /commission_total/i.test(error.message) &&
      /does not exist|não existe|schema cache|could not find/i.test(error.message)
    if (!missingColumn) throw error

    delete payload.commission_total
    const retry = await supabase.from(FOLDERS_TABLE).update(payload).eq('id', input.folderId)
    if (retry.error) throw retry.error
  },

  /** Apaga pasta em processing (e linhas via CASCADE) após falha. */
  async abortPasta(folderId: string): Promise<void> {
    const { error } = await supabase.from(FOLDERS_TABLE).delete().eq('id', folderId)
    if (error) throw error
  },

  /** Consulta paginada das linhas já gravadas. */
  async listComissoes(
    filters: FinanceiroComissoesConsultaFilters
  ): Promise<FinanceiroComissoesConsultaResult> {
    const page = Math.max(1, filters.page)
    const pageSize = Math.max(1, Math.min(100, filters.pageSize))
    const from = (page - 1) * pageSize
    const to = from + pageSize - 1

    const folderNames = selectedFolderNames(filters)
    const hasSelectiveFilter = folderNames.length > 0 || hasLineFilter(filters)
    const pagamento = filters.dataPagamentoEfetivo?.trim()
    // Sem filtro: count exact + sort em coluna sem índice estoura timeout em tabela grande.
    // Com data de pagamento, created_at + LIMIT faz o plano percorrer a tabela inteira
    // até achar 20 linhas daquela data. Ordenar pela própria data usa o índice e para no LIMIT.
    const countMode = hasSelectiveFilter ? 'exact' : 'estimated'
    const requestedSort =
      filters.sortBy && COMISSAO_SORT_COLUMNS.has(filters.sortBy) ? filters.sortBy : 'created_at'
    const sortBy = !hasSelectiveFilter
      ? 'id'
      : pagamento && requestedSort === 'created_at'
        ? 'data_pagamento_efetivo'
        : requestedSort
    const ascending = !hasSelectiveFilter
      ? false
      : pagamento && requestedSort === 'created_at'
        ? true
        : (filters.sortOrder ?? 'desc') === 'asc'

    const ordered = (source: ComissaoOrderedQuery): ComissaoOrderedQuery => {
      let dataQuery = source.order(sortBy, { ascending })
      // id extra impede o índice de data_pagamento_efetivo de parar no LIMIT.
      if (sortBy !== 'id' && sortBy !== 'data_pagamento_efetivo') {
        dataQuery = dataQuery.order('id', { ascending })
      }
      return dataQuery
    }

    let data: unknown
    let error: { message?: string } | null
    let count: number | null

    if (pagamento) {
      // Contagem e página separadas: o count exact junto do SELECT * materializa
      // todas as linhas da data e estoura o statement_timeout.
      const countQuery = applyComissaoFilters(
        supabase.from(ROWS_TABLE).select('id', { count: 'exact', head: true }) as unknown as ComissaoFilterQuery,
        filters
      ) as unknown as Promise<{ count: number | null; error: { message?: string } | null }>
      const pageQuery = ordered(
        applyComissaoFilters(
          supabase.from(ROWS_TABLE).select('*') as unknown as ComissaoFilterQuery,
          filters
        ) as unknown as ComissaoOrderedQuery
      )
      const [countResult, pageResult] = await Promise.all([
        countQuery,
        pageQuery.range(from, to),
      ])
      if (countResult.error) {
        throw new Error(countResult.error.message || 'Erro ao carregar comissões.')
      }
      data = pageResult.data
      error = pageResult.error
      count = countResult.count
    } else {
      const pageQuery = ordered(
        applyComissaoFilters(
          supabase.from(ROWS_TABLE).select('*', { count: countMode }) as unknown as ComissaoFilterQuery,
          filters
        ) as unknown as ComissaoOrderedQuery
      )
      const pageResult = await pageQuery.range(from, to)
      data = pageResult.data
      error = pageResult.error
      count = pageResult.count
    }

    if (error) {
      throw new Error(error.message || 'Erro ao carregar comissões.')
    }
    return {
      rows: (data ?? []) as FinanceiroComissoesIcatuLinha[],
      total: count ?? 0,
    }
  },

  /**
   * Totais do filtro inteiro.
   * Sem filtro de linha, usa commission_total das pastas.
   * Pasta com linhas e total ainda 0 chama finan_icatu_soma_comissao
   * (agregação do PostgREST está desligada e a soma da tabela inteira estoura o timeout).
   */
  async resumoComissoes(
    filters: FinanceiroComissoesConsultaFilters
  ): Promise<FinanceiroComissoesResumo> {
    const folderNames = selectedFolderNames(filters)
    const { rows, hasCommissionColumn } = await listPastaTotals(folderNames)
    const totalRegistros = rows.reduce((sum, row) => sum + row.row_count, 0)

    if (!hasLineFilter(filters)) {
      const pending = rows.filter((row) => pastaPrecisaSomar(row, hasCommissionColumn))
      if (pending.length === 0) {
        return {
          totalRegistros,
          totalComissao: rows.reduce((sum, row) => sum + (row.commission_total ?? 0), 0),
        }
      }

      const computed = await mapPool(pending, 3, async (row) => {
        const total = await sumValorComissao(filters, row.folder_name)
        return { folderName: row.folder_name, total }
      })
      if (hasCommissionColumn) {
        await Promise.all(
          computed.map((item) => saveFolderCommissionTotal(item.folderName, item.total))
        )
      }

      const byName = new Map(computed.map((item) => [item.folderName, item.total]))
      const totalComissao = rows.reduce((sum, row) => {
        const computedTotal = byName.get(row.folder_name)
        return sum + (computedTotal ?? row.commission_total ?? 0)
      }, 0)
      return { totalRegistros, totalComissao }
    }

    const names =
      rows.length > 0
        ? rows.map((row) => row.folder_name)
        : folderNames
    if (names.length === 0) {
      return { totalRegistros: null, totalComissao: 0 }
    }
    const parts = await mapPool(names, 3, (folderName) =>
      sumValorComissao(filters, folderName)
    )
    return {
      totalRegistros: null,
      totalComissao: parts.reduce((sum, value) => sum + value, 0),
    }
  },

  /**
   * Agrega comissão × extrato por dia no(s) período(s).
   * Comissão: finan_icatu_soma_comissao_dedup_periodo (dedup entre pastas).
   * Extrato: finan_pel_soma_extrato_periodo (financeiro_09).
   */
  async listAnaliseComparativa(
    filters: FinanceiroAnaliseComparativaFilters
  ): Promise<FinanceiroAnaliseComparativaResult> {
    const comissaoDe = isoDateOrNull(filters.dataPagamentoEfetivoDe)
    const comissaoAte = isoDateOrNull(filters.dataPagamentoEfetivoAte)
    const extratoDe = isoDateOrNull(filters.extratoDataDe)
    const extratoAte = isoDateOrNull(filters.extratoDataAte)

    const comissaoPeriodo = resolvePeriodo(comissaoDe, comissaoAte, 'pagamento efetivo')
    const extratoPeriodo = resolvePeriodo(extratoDe, extratoAte, 'extrato')

    if (!comissaoPeriodo && !extratoPeriodo) {
      throw new Error(
        'Informe o período do Pagamento Efetivo e/ou o período do extrato para executar a análise.'
      )
    }

    const cpfRaw = filters.cpfCnpj?.trim() || ''
    const documento = cpfRaw ? documentoConsulta(cpfRaw) : null
    const documentoVariants =
      documento?.variants ??
      (cpfRaw
        ? [...new Set([cpfRaw, cpfRaw.replace(/[.\-\/\s]/g, '')].filter(Boolean))]
        : null)
    const account = filters.account?.trim() || null
    const identification = filters.identification?.trim() || null
    const type =
      filters.type === 'debit' || filters.type === 'credit' ? filters.type : null
    const tipoComissao = filters.tipoComissao?.trim() || null
    const statusRecibo = filters.statusRecibo?.trim() || null

    // Só um período: usa o mesmo intervalo nos dois lados (como na data única).
    const periodoComissao = comissaoPeriodo ?? extratoPeriodo
    const periodoExtrato = extratoPeriodo ?? comissaoPeriodo

    const [comissaoRows, extratoRows] = await Promise.all([
      periodoComissao
        ? sumValorComissaoDedupPeriodo({
            de: periodoComissao.de,
            ate: periodoComissao.ate,
            tipoComissao,
            statusRecibo,
            documentoVariants,
          })
        : Promise.resolve([] as Array<{ dia: string; total: number }>),
      periodoExtrato
        ? financeiroExtratosContaService.sumAmountByPeriod({
            de: periodoExtrato.de,
            ate: periodoExtrato.ate,
            account,
            identification,
            type,
          })
        : Promise.resolve([] as Array<{ dia: string; total: number }>),
    ])

    const comissaoMap = new Map(comissaoRows.map((row) => [row.dia, row.total]))
    const extratoMap = new Map(extratoRows.map((row) => [row.dia, row.total]))
    const dias = [...new Set([...comissaoMap.keys(), ...extratoMap.keys()])].sort((a, b) =>
      a.localeCompare(b)
    )

    const lines: FinanceiroAnaliseComparativaLinha[] = dias
      .map((dia) => ({
        dia,
        totalComissao: comissaoMap.get(dia) ?? 0,
        totalExtrato: extratoMap.get(dia) ?? 0,
      }))
      .filter((row) => row.totalComissao !== 0 || row.totalExtrato !== 0)

    return { rows: lines }
  },

  /**
   * Itens deduplicados que compõem Comissão Icatu (R$) de um dia.
   * Requer frontend/sqls/financeiro_11_analise_comparativa_itens_comissao.sql.
   */
  async listItensComissaoDia(
    filters: FinanceiroAnaliseComissaoItensFilters
  ): Promise<FinanceiroAnaliseComissaoItensResult> {
    const dia = isoDateOrNull(filters.dia)
    if (!dia) {
      throw new Error('Informe a data do pagamento efetivo para listar os itens.')
    }

    const page = Math.max(1, filters.page ?? 1)
    const pageSize = Math.max(1, Math.min(5000, filters.pageSize ?? 50))
    const offset = (page - 1) * pageSize

    const cpfRaw = filters.cpfCnpj?.trim() || ''
    const documento = cpfRaw ? documentoConsulta(cpfRaw) : null
    const documentoVariants =
      documento?.variants ??
      (cpfRaw
        ? [...new Set([cpfRaw, cpfRaw.replace(/[.\-\/\s]/g, '')].filter(Boolean))]
        : null)

    const { data, error } = await supabase.rpc('finan_icatu_itens_comissao_dedup_dia', {
      p_dia: dia,
      p_tipo_comissao: filters.tipoComissao?.trim() || null,
      p_status_recibo: filters.statusRecibo?.trim() || null,
      p_documento:
        documentoVariants && documentoVariants.length > 0 ? documentoVariants : null,
      p_limit: pageSize,
      p_offset: offset,
    })

    if (error) {
      const message = error.message || 'Erro ao carregar os itens da comissão.'
      if (
        /finan_icatu_itens_comissao_dedup_dia|Could not find the function|schema cache/i.test(
          message
        )
      ) {
        throw new Error(
          'O detalhe da comissão ainda não está disponível. Execute frontend/sqls/financeiro_11_analise_comparativa_itens_comissao.sql no Supabase SQL Editor.'
        )
      }
      throw new Error(message)
    }

    const raw = (data ?? []) as Array<{
      id?: string | null
      nome_corretor?: string | null
      cpf_cnpj?: string | null
      apolice?: string | null
      fatura?: string | null
      nr_cobranca?: string | null
      tipo_comissao?: string | null
      valor_comissao?: number | string | null
      data_pagamento_efetivo?: string | null
      status_recibo?: string | null
      folder_name?: string | null
      total_count?: number | string | null
    }>

    const total = raw.length > 0 ? Number(raw[0].total_count ?? 0) || 0 : 0
    const rows: FinanceiroAnaliseComissaoItem[] = raw.map((row) => ({
      id: String(row.id ?? ''),
      nomeCorretor: String(row.nome_corretor ?? ''),
      cpfCnpj: String(row.cpf_cnpj ?? ''),
      apolice: String(row.apolice ?? ''),
      fatura: String(row.fatura ?? ''),
      nrCobranca: String(row.nr_cobranca ?? ''),
      tipoComissao: String(row.tipo_comissao ?? ''),
      valorComissao: Number(row.valor_comissao ?? 0) || 0,
      dataPagamentoEfetivo: String(row.data_pagamento_efetivo ?? ''),
      statusRecibo: String(row.status_recibo ?? ''),
      folderName: String(row.folder_name ?? ''),
    }))

    return { rows, total }
  },
}

function isoDateOrNull(value: string | null | undefined): string | null {
  const raw = value?.trim() || ''
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null
  return raw
}

function resolvePeriodo(
  de: string | null,
  ate: string | null,
  label: string
): { de: string; ate: string } | null {
  if (!de && !ate) return null
  if (!de || !ate) {
    throw new Error(`Informe a data inicial e a data final do período de ${label}.`)
  }
  if (ate < de) {
    throw new Error(`A data final do período de ${label} deve ser maior ou igual à inicial.`)
  }
  const start = new Date(`${de}T00:00:00`)
  const end = new Date(`${ate}T00:00:00`)
  const days = Math.round((end.getTime() - start.getTime()) / 86400000)
  if (days > 366) {
    throw new Error(`O período de ${label} não pode ultrapassar 366 dias.`)
  }
  return { de, ate }
}

type PastaTotalRow = {
  folder_name: string
  row_count: number
  commission_total: number | null
}

function moneyOrNull(value: unknown): number | null {
  if (value == null || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function isMissingCommissionTotalColumn(message: string): boolean {
  return (
    /commission_total/i.test(message) &&
    /does not exist|não existe|schema cache|could not find/i.test(message)
  )
}

/** Total 0 com linhas gravadas ainda não foi preenchido (default da coluna). */
function pastaPrecisaSomar(row: PastaTotalRow, hasCommissionColumn: boolean): boolean {
  if (row.row_count <= 0) return false
  if (!hasCommissionColumn || row.commission_total == null) return true
  return row.commission_total === 0
}

async function listPastaTotals(folderNames: string[]): Promise<{
  rows: PastaTotalRow[]
  hasCommissionColumn: boolean
}> {
  const run = (columns: string) => {
    let query = supabase.from(FOLDERS_TABLE).select(columns).eq('status', 'complete')
    if (folderNames.length === 1) query = query.eq('folder_name', folderNames[0])
    else if (folderNames.length > 1) query = query.in('folder_name', folderNames)
    return query
  }

  const mapRows = (
    data: unknown,
    hasCommissionColumn: boolean
  ): PastaTotalRow[] => {
    const source = (data ?? []) as Array<{
      folder_name?: string | null
      row_count?: number | null
      commission_total?: number | string | null
    }>
    return source
      .map((row) => ({
        folder_name: String(row.folder_name ?? '').trim(),
        row_count: Number(row.row_count) || 0,
        commission_total: hasCommissionColumn ? moneyOrNull(row.commission_total) : null,
      }))
      .filter((row) => row.folder_name.length > 0)
  }

  const withTotal = await run('folder_name, row_count, commission_total')
  if (!withTotal.error) {
    return { rows: mapRows(withTotal.data, true), hasCommissionColumn: true }
  }

  if (!isMissingCommissionTotalColumn(withTotal.error.message)) throw withTotal.error

  const countsOnly = await run('folder_name, row_count')
  if (countsOnly.error) throw countsOnly.error
  return { rows: mapRows(countsOnly.data, false), hasCommissionColumn: false }
}

async function saveFolderCommissionTotal(folderName: string, total: number): Promise<void> {
  const { error } = await supabase
    .from(FOLDERS_TABLE)
    .update({ commission_total: total })
    .eq('folder_name', folderName)
    .eq('status', 'complete')
  if (!error) return
  if (isMissingCommissionTotalColumn(error.message)) return
  console.error(error)
}

async function mapPool<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return []
  const results = new Array<R>(items.length)
  let next = 0
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next
      next += 1
      results[index] = await fn(items[index])
    }
  })
  await Promise.all(workers)
  return results
}

async function sumValorComissao(
  filters: FinanceiroComissoesConsultaFilters,
  folderName: string
): Promise<number> {
  const pagamento = filters.dataPagamentoEfetivo?.trim() || ''
  const dataExact = pagamento ? dataPagamentoEfetivoLookups(pagamento) : null

  const search = filters.search?.trim() || ''
  const documento = search ? documentoConsulta(search) : null
  const searchTerm = search && !documento ? escapeIlike(search.replace(/,/g, ' ')) : null

  const { data, error } = await supabase.rpc('finan_icatu_soma_comissao', {
    p_folder_name: folderName,
    p_data_exact: dataExact,
    p_data_ilike: null,
    p_tipo_comissao: filters.tipoComissao?.trim() || null,
    p_status_recibo: filters.statusRecibo?.trim() || null,
    p_documento: documento?.variants ?? null,
    p_search: searchTerm,
    p_persist: !hasLineFilter(filters),
  })

  if (error) {
    const message = error.message || 'Erro ao somar o valor da comissão.'
    if (/finan_icatu_soma_comissao|Could not find the function|schema cache/i.test(message)) {
      throw new Error(
        'O total da comissão ainda não está disponível. Execute frontend/sqls/financeiro_10_comissoes_soma_pasta.sql no Supabase SQL Editor.'
      )
    }
    throw new Error(message)
  }

  const value = typeof data === 'number' ? data : Number(data ?? 0)
  return Number.isFinite(value) ? value : 0
}

/** Soma por dia no período, sem duplicar chave de negócio entre pastas/arquivos. */
async function sumValorComissaoDedupPeriodo(input: {
  de: string
  ate: string
  tipoComissao?: string | null
  statusRecibo?: string | null
  documentoVariants?: string[] | null
}): Promise<Array<{ dia: string; total: number }>> {
  const { data, error } = await supabase.rpc('finan_icatu_soma_comissao_dedup_periodo', {
    p_data_de: input.de,
    p_data_ate: input.ate,
    p_tipo_comissao: input.tipoComissao?.trim() || null,
    p_status_recibo: input.statusRecibo?.trim() || null,
    p_documento:
      input.documentoVariants && input.documentoVariants.length > 0
        ? input.documentoVariants
        : null,
  })

  if (error) {
    const message = error.message || 'Erro ao somar o valor da comissão.'
    if (
      /finan_icatu_soma_comissao_dedup_periodo|Could not find the function|schema cache/i.test(
        message
      )
    ) {
      throw new Error(
        'A soma por período ainda não está disponível. Execute frontend/sqls/financeiro_09_analise_comparativa.sql no Supabase SQL Editor.'
      )
    }
    throw new Error(message)
  }

  return (data ?? []).map(
    (row: { dia?: string | null; total_comissao?: number | string | null }) => ({
      dia: String(row.dia ?? '').slice(0, 10),
      total: Number(row.total_comissao ?? 0) || 0,
    })
  ).filter((row: { dia: string; total: number }) => Boolean(row.dia))
}
